import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal, ViewChild } from '@angular/core';
import { AddDocumentFormComponent } from '@lib/components/investisseur-details/tabs/documents/add-document-form/add-document-form.component';
import { DocumentsTableComponent } from '@lib/components/investisseur-details/tabs/documents/documents-table/documents-table.component';
import { ExtendedFileMetadata } from '@lib/models/extended-file-metadata';
import { FileDocument } from '@lib/models/file-document.model';
import {
  DocumentType,
  EtatDer,
  EtatQpi,
  EtatRecueil,
  FileMetadata,
  GedDocumentType,
  Investisseur,
  SignatureState,
} from '@lib/models/generated/graphql';
import { DocumentsService } from '@lib/services/documents.service';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { ReferenceDataService } from '@lib/services/reference-data.service';
import { getRawFileId } from '@lib/utils/app-utils';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { filter, firstValueFrom, forkJoin, take } from 'rxjs';
import { AddDocumentTypeEnum } from '../add-document-type.enum';
import { OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-add-document-signature',
  imports: [SHARED_IMPORTS, DocumentsTableComponent, AddDocumentFormComponent],
  templateUrl: './add-document-signature.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-document-signature.component.scss',
})
export class AddDocumentSignatureComponent implements OnInit, OnChanges {
  @ViewChild('documentsTableComponent', { static: false }) documentsTableComponent: DocumentsTableComponent;
  @ViewChild('addDocumentFormComponent', { static: false }) addDocumentFormComponent: AddDocumentFormComponent;
  private investisseurService = inject(InvestisseurService);
  private documentService = inject(DocumentsService);
  private referenceDataService = inject(ReferenceDataService);
  public addDocumentType = input<AddDocumentTypeEnum>();
  public documentsToDismiss = input<string[]>([]);
  public operationInvestors = input<Investisseur[]>([]);
  public documentTypeCode = input<string>();
  public singleSelection = input(false);
  public excludedDocumentTypeCodes = input<string[]>([]);
  public signedOnly = input(false);
  public documents = signal<ExtendedFileMetadata[]>([]);
  public documentTypes = signal<DocumentType[]>([]);
  public addDocumentTypeEnum = AddDocumentTypeEnum;
  public investisseurDocuments = signal<FileMetadata[]>([]);
  public derSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(doc => doc.typeDocumentCode === GedDocumentType.Der && doc.etatSignature === SignatureState.Pending)
  );
  public recueilSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(
      doc => doc.typeDocumentCode === GedDocumentType.RecInfo && doc.etatSignature === SignatureState.Pending
    )
  );
  public qpiSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(
      doc => doc.typeDocumentCode === GedDocumentType.ProfInvest && doc.etatSignature === SignatureState.Pending
    )
  );
  public recueilImcomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.recueilEtat === EtatRecueil.ACompleter
  );
  public qpiImcomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.qpiEtat === EtatQpi.ACompleter
  );
  public derIncomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.derEtat === EtatDer.ACompleter
  );

  public derNotSignedOrReadyToSigned = computed<boolean>(
    () =>
      ![EtatDer.Signe, EtatDer.ASigner].includes(
        this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.derEtat as EtatDer
      )
  );

  ngOnInit(): void {
    this.loadDocuments();
  }

  private loadDocuments(): void {
    if (this.addDocumentType() === AddDocumentTypeEnum.FromDocumentMinotaure) {
      if (this.operationInvestors().length > 0) {
        void this.getOperationInvestorDocuments();
      } else {
        this.getInvestisseurDocuments();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['addDocumentType'] && !changes['addDocumentType'].firstChange) {
      this.loadDocuments();
    }
  }

  public getSelectedDocuments(): FileDocument[] {
    if (this.addDocumentType() === AddDocumentTypeEnum.FromDocumentMinotaure) {
      return this.documentsTableComponent?.selection.selected.map(d => {
        const file = FileDocument.fromFileMetadata(d, d.typeDocumentLabel);
        file.fromDocumentMinotaure = true;
        return file;
      });
    } else if (this.addDocumentType() === AddDocumentTypeEnum.FromLocalFolder) {
      return this.addDocumentFormComponent.formArray.valid
        ? this.addDocumentFormComponent.formArray.value.map(d => {
            const document = new FileDocument(
              d.file?.name as string,
              new Date(),
              d.documentType as string,
              undefined,
              d.documentTypeLabel as string
            );
            document.file = d.file;
            document.dateReference = d.referenceDate ? new Date(d.referenceDate) : undefined;
            return document;
          })
        : [];
    }
    return [];
  }

  public getSelectedFileMetadata(): ExtendedFileMetadata | undefined {
    return this.documentsTableComponent?.selection.selected[0];
  }

  private getInvestisseurDocuments(): void {
    forkJoin({
      documentTypes: this.referenceDataService.getDocumentTypes().pipe(take(1)),
      documents: this.investisseurService.investisseurDocuments$.pipe(
        filter(docs => docs !== null),
        take(1)
      ),
    }).subscribe(({ documentTypes, documents }) => {
      this.investisseurDocuments.set(documents);
      this.documentTypes.set(documentTypes);
      const extendedFiles: ExtendedFileMetadata[] = (documents ?? [])
        .filter(
          f =>
            documentTypes.some(dt => dt.gedTypeCode === f.typeDocumentCode) &&
            !this.excludedDocumentTypeCodes().includes(f.typeDocumentCode ?? '') &&
            (!this.documentTypeCode() || f.typeDocumentCode === this.documentTypeCode()) &&
            f.extension === 'pdf' &&
            this.matchesSignatureFilter(f) &&
            !this.documentsToDismiss().includes(getRawFileId(f.fileId as string)) &&
            (this.signedOnly() || this.filterConnaissanceClientDocs(f))
        )
        .map(f => ({
          ...f,
          typeDocumentLabel: this.getTypeLabel(f.typeDocumentCode as string),
          baseFileId: getRawFileId(f.fileId as string),
          file: new File([], f.fileName as string),
        }));
      this.documents.set(extendedFiles);
    });
  }

  private async getOperationInvestorDocuments(): Promise<void> {
    const investorIds = [
      ...new Set(
        this.operationInvestors()
          .map(investor => Number(investor.id))
          .filter(Number.isFinite)
      ),
    ];
    try {
      const [documentTypes, documentsByInvestor] = await Promise.all([
        firstValueFrom(this.referenceDataService.getDocumentTypes().pipe(take(1))),
        Promise.all(investorIds.map(investorId => this.documentService.getInvestisseurDocumentsByInvestisseurId(investorId))),
      ]);
      this.documentTypes.set(documentTypes);

      const documentsById = new Map<string, FileMetadata>();
      for (const [index, documents] of documentsByInvestor.entries()) {
        for (const document of documents ?? []) {
          const fileId = getRawFileId(document.fileId);
          if (!fileId) continue;
          const owners = new Set([
            ...(document.investisseurIds ?? []).map(Number),
            ...(document.investisseurId ? [Number(document.investisseurId)] : []),
          ]);
          if (owners.size === 0) owners.add(investorIds[index]);
          const current = documentsById.get(fileId);
          documentsById.set(fileId, {
            ...(current ?? document),
            investisseurIds: [...new Set([...(current?.investisseurIds ?? []), ...owners])],
          });
        }
      }

      const dismissedIds = new Set(this.documentsToDismiss());
      const extendedFiles: ExtendedFileMetadata[] = [...documentsById.values()]
        .filter(
          document =>
          documentTypes.some(type => type.gedTypeCode === document.typeDocumentCode) &&
          !this.excludedDocumentTypeCodes().includes(document.typeDocumentCode ?? '') &&
            (!this.documentTypeCode() || document.typeDocumentCode === this.documentTypeCode()) &&
            document.extension?.toLowerCase() === 'pdf' &&
            this.matchesSignatureFilter(document) &&
            !dismissedIds.has(getRawFileId(document.fileId))
        )
        .map(document => ({
          ...document,
          typeDocumentLabel: this.getTypeLabel(document.typeDocumentCode as string),
          baseFileId: getRawFileId(document.fileId as string),
          file: new File([], document.fileName as string),
        }));

      this.investisseurDocuments.set([...documentsById.values()]);
      this.documents.set(extendedFiles);
    } catch (error) {
      console.error('Unable to load operation investor documents for signature.', error);
      this.documents.set([]);
    }
  }

  private matchesSignatureFilter(file: FileMetadata): boolean {
    // Les signatures manuscrites ont une date GED sans forcément avoir un état électronique.
    const signed = file.etatSignature === SignatureState.Completed || (!!file.dateSignature && !file.etatSignature);
    return this.signedOnly() ? signed : !file.etatSignature;
  }

  private filterConnaissanceClientDocs(file: FileMetadata): boolean {
    if (file.typeDocumentCode === GedDocumentType.RecInfo && (this.recueilImcomplete() || this.recueilSignatureInProgress())) {
      return false;
    }
    if (file.typeDocumentCode === GedDocumentType.ProfInvest && (this.qpiImcomplete() || this.qpiSignatureInProgress())) {
      return false;
    }
    if (file.typeDocumentCode === GedDocumentType.Der && (this.derIncomplete() || this.derSignatureInProgress())) {
      return false;
    }
    if (
      [GedDocumentType.RecInfo, GedDocumentType.ProfInvest].includes(file.typeDocumentCode as GedDocumentType) &&
      this.derNotSignedOrReadyToSigned()
    ) {
      return false;
    }
    return true;
  }

  private getTypeLabel(code: string): string {
    return this.documentTypes()?.find(dt => dt.gedTypeCode === code)?.label ?? code;
  }
}
